import React, { useCallback, useEffect, useRef } from 'react';
import '../../../css/print.css';
import { useParams } from 'react-router-dom';
import { useTreatmentPrint } from './hooks/useTreatmentPrint';

export default function TreatmentToPrint() {
  const { treatmentId } = useParams();

  const { treatmentToPrint, logo, fetchTreatmentToPrint, fetchLogo } = useTreatmentPrint();

  const hasPrinted = useRef(false);

  const triggerPrint = useCallback(() => {
    if (hasPrinted.current) {
      return;
    }
    hasPrinted.current = true;
    window.print();
  }, []);

  const getTreatmentToPrint = useCallback(
    () => fetchTreatmentToPrint(treatmentId),
    [fetchTreatmentToPrint, treatmentId],
  );

  const getLogo = useCallback(() => fetchLogo(), [fetchLogo]);

  useEffect(() => {
    const handleAfterPrint = () => {
      window.close();
    };
    document.body.classList.add('printing-active');
    window.addEventListener('afterprint', handleAfterPrint);

    // Fallback: imprime mesmo que alguma chamada trave ou demore demais.
    const fallbackTimer = setTimeout(triggerPrint, 4000);

    // Só imprime depois que os dados e a logo (base64) estiverem prontos.
    Promise.all([getTreatmentToPrint(), getLogo()]).finally(() => {
      setTimeout(triggerPrint, 300);
    });

    return () => {
      clearTimeout(fallbackTimer);
      window.removeEventListener('afterprint', handleAfterPrint);
    };
  }, [getTreatmentToPrint, getLogo, triggerPrint]);

  return (
    <div className="container text-center d-none d-print-block">
      <img src={logo || treatmentToPrint?.urlFromLogo} width={200} height={200} alt="" />
      <div className="mt-5">
        <h5>Previsão de custo:</h5>

        <form className="mt-3">
          <div>
            <span>
              <strong>Paciente: </strong>
              {treatmentToPrint?.patientName}
            </span>
          </div>

          <div className="mt-2">
            <span>
              <strong>Tratamento: </strong>
              {treatmentToPrint?.treatmentDescription}
            </span>
          </div>

          <table className="table mt-3">
            <thead>
              <tr>
                <th scope="col">Procedimento</th>
                <th scope="col">Valor</th>
              </tr>
            </thead>
            <tbody>
              {treatmentToPrint.procedures?.map((procedure) => {
                return (
                  <tr key={procedure.id}>
                    <td>
                      {procedure.description} - Dente {procedure.fullToothName}
                      {procedure.observation && (
                        <>
                          <br />({procedure.observation})
                        </>
                      )}
                    </td>
                    <td>{procedure.formattedAmount}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div className="mt-2">
            <span>
              <strong>Valor Total:</strong> {treatmentToPrint?.formattedTotalAmount}
            </span>
          </div>

          <div className="mt-4">
            <p className="fs--1 mb-0">{treatmentToPrint?.observationOneFromPrintConfig}</p>
          </div>

          <div className="mt-4">
            <p className="fs--1 mb-0">{treatmentToPrint?.titleOneFromPrintConfig}</p>
          </div>
          <div className="mt-1">
            <p className="fs--1 mb-0">{treatmentToPrint?.contentOneFromPrintConfig}</p>
          </div>

          <div className="mt-6">
            <p className="fs--1 mb-0">
              {treatmentToPrint?.cityName}, {treatmentToPrint?.formattedActualDate}.
            </p>
          </div>

          <div className="mt-1">
            <p className="fs--1 mb-0">{treatmentToPrint?.clinicName}</p>
          </div>
          <div className="mt-1">
            <p className="fs--1 mb-0">{treatmentToPrint?.email}</p>
          </div>
          <div className="mt-1">
            <p className="fs--1 mb-0">{treatmentToPrint?.phoneNumber}</p>
          </div>
        </form>
      </div>
    </div>
  );
}
